const INTERACTIONS = new Set(['wheel', 'drag', 'slider']);
const DIRECTIONS = new Set(['forward', 'backward']);
const INPUT_EVENT_KINDS = Object.freeze({
  wheel: 'wheel',
  drag: 'pointer-move',
  slider: 'slider-change',
});
const RELEASE_EVENT_KINDS = Object.freeze({
  wheel: 'wheel-quiet',
  drag: 'pointer-up',
  slider: 'slider-release',
});
const MAX_CANDIDATES = 256;
const MAX_TRANSITIONS = 512;
const MAX_NON_TERMINAL_TRANSITIONS = MAX_TRANSITIONS - 1;

const exactData = (value, keys) => {
  try {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const ownKeys = Reflect.ownKeys(value);
    if (ownKeys.length !== keys.length
        || ownKeys.some(key => typeof key !== 'string' || !keys.includes(key))) return null;
    const snapshot = Object.create(null);
    for (const key of keys) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
      snapshot[key] = descriptor.value;
    }
    return Object.freeze(snapshot);
  } catch (_) {
    return null;
  }
};

const nonNegativeInteger = value => Number.isSafeInteger(value) && value >= 0;
const positiveInteger = value => Number.isSafeInteger(value) && value > 0;

// This factory is intentionally useful only to the bridge instance that owns its
// private authorization, row, drain, render, and ordinal callbacks. A separately
// constructed controller cannot mint a bridge-owned report drain.
export const createViewportCineInteractionController = dependencies => {
  const {
    appendRetainedRow,
    cancelFrame,
    clearTimer,
    isAuthorizationCurrent,
    observeTrustedBegin,
    observeTrustedInput,
    observeTrustedRelease,
    observeTrustedZeroWheel,
    readClock,
    reserveOrdinal,
    requestFrame,
    setTimer,
  } = dependencies;
  const interactionStates = new WeakMap();
  const inputStates = new WeakMap();
  const commandStates = new WeakMap();
  const renderBindingStates = new WeakMap();
  const publicationStates = new WeakMap();
  const activityReceiptStates = new WeakMap();
  const retainedRows = new WeakSet();
  const retainedDrains = new WeakSet();
  const trustedEventBindings = new WeakMap();
  const activeOwnersByProofKey = new Map();

  const proofKeyFor = state => (
    `${state.originPhaseId}:${state.viewport}:${state.interaction}:${state.direction}`
  );

  const releaseProofKeyOwnership = state => {
    if (!state?.proofKey || activeOwnersByProofKey.get(state.proofKey) !== state) return false;
    activeOwnersByProofKey.delete(state.proofKey);
    return true;
  };

  const stateIsCurrent = state => {
    try {
      return Boolean(state?.active && isAuthorizationCurrent(state.authorization) === true);
    } catch (_) {
      return false;
    }
  };

  // A trusted event may bind activity once and then mint one input only for
  // that exact interaction. Direct input binds and consumes immediately; any
  // later activity, foreign handle, or second input remains fail closed.
  const bindTrustedActivityEvent = (state, event) => {
    try {
      if (trustedEventBindings.has(event)) return false;
      trustedEventBindings.set(event, Object.freeze({ state, inputConsumed: false }));
      return true;
    } catch (_) {
      return false;
    }
  };

  const consumeTrustedInputEvent = (state, event) => {
    try {
      const binding = trustedEventBindings.get(event);
      if (binding && (binding.state !== state || binding.inputConsumed)) return false;
      trustedEventBindings.set(event, Object.freeze({ state, inputConsumed: true }));
      return true;
    } catch (_) {
      return false;
    }
  };

  const append = (state, event) => {
    if (!stateIsCurrent(state) || state.transitionCount >= MAX_NON_TERMINAL_TRANSITIONS) return null;
    const row = appendRetainedRow(state.authorization, Object.freeze({
      phase: 'cine',
      originPhaseId: state.originPhaseId,
      cineId: state.cineId,
      viewport: state.viewport,
      seriesGeneration: state.seriesGeneration,
      interaction: state.interaction,
      direction: state.direction,
    }), Object.freeze(event));
    if (!row) return null;
    state.transitionCount += 1;
    retainedRows.add(row);
    return row;
  };

  const clearQuietTimer = state => {
    if (!state) return;
    if (state.quietTimer != null) {
      try { clearTimer(state.quietTimer); } catch (_) {}
      state.quietTimer = null;
    }
    state.latestActivityToken = null;
    state.latestActivityAtMs = null;
  };

  const cancelQueuedFrame = publicationState => {
    if (!publicationState || publicationState.frameRequestId == null) return false;
    const frameRequestId = publicationState.frameRequestId;
    publicationState.frameRequestId = null;
    try { cancelFrame(frameRequestId); } catch (_) {}
    return true;
  };

  const close = (state, reason) => {
    if (!state?.active || !['failed', 'cancelled', 'replaced', 'overflow'].includes(reason)) return false;
    clearQuietTimer(state);
    cancelQueuedFrame(state.currentCommand?.publication);
    // Terminal closure abandons controller-owned command work. It does not
    // manufacture publication, paint, settlement, or completed phase-work rows.
    state.outstandingWorkIds.clear();
    state.active = false;
    state.outcome = ['failed', 'overflow'].includes(reason) ? 'failed' : 'cancelled';
    state.overflow = reason === 'overflow';
    releaseProofKeyOwnership(state);
    let atMs = null;
    try { atMs = readClock(state.clock); } catch (_) { atMs = null; }
    appendRetainedRow(state.authorization, Object.freeze({
      phase: 'cine', originPhaseId: state.originPhaseId, cineId: state.cineId,
      viewport: state.viewport, seriesGeneration: state.seriesGeneration,
      interaction: state.interaction, direction: state.direction,
    }), Object.freeze({
      type: 'cine-terminal',
      reason,
      atMs: atMs === null ? state.lastAtMs : Math.max(state.lastAtMs, atMs),
    }));
    return true;
  };

  const authorityCurrent = state => {
    if (!stateIsCurrent(state)) return false;
    for (const callback of state.authorityCallbacks) {
      if (!stateIsCurrent(state)) return false;
      let current = false;
      try { current = Reflect.apply(callback, undefined, []) === true; } catch (_) { current = false; }
      if (!current || !stateIsCurrent(state)) return false;
    }
    return stateIsCurrent(state);
  };

  const readMonotonic = (state, strict = false) => {
    const atMs = readClock(state.clock);
    if (atMs === null || (strict ? atMs <= state.lastAtMs : atMs < state.lastAtMs)) return null;
    state.lastAtMs = atMs;
    return atMs;
  };

  const begin = options => {
    const keys = [
      'authorization', 'event', 'clock', 'phase', 'originPhaseId', 'viewport', 'seriesGeneration',
      'interaction', 'direction', 'initialSlice', 'isRouteCurrent', 'isSeriesCurrent',
      'isGenerationCurrent', 'isVisible', 'isFocused', 'isActiveViewport',
    ];
    const snapshot = exactData(options, keys);
    if (!snapshot || snapshot.phase !== 'cine' || !positiveInteger(snapshot.originPhaseId)
        || !nonNegativeInteger(snapshot.viewport) || !positiveInteger(snapshot.seriesGeneration)
        || !INTERACTIONS.has(snapshot.interaction) || !DIRECTIONS.has(snapshot.direction)
        || !nonNegativeInteger(snapshot.initialSlice)
        || [snapshot.isRouteCurrent, snapshot.isSeriesCurrent, snapshot.isGenerationCurrent,
          snapshot.isVisible, snapshot.isFocused, snapshot.isActiveViewport]
          .some(callback => typeof callback !== 'function')
        || isAuthorizationCurrent(snapshot.authorization) !== true) return null;
    const beginObservation = observeTrustedBegin(
      snapshot.event,
      snapshot.clock,
      snapshot.interaction,
    );
    if ((snapshot.interaction === 'drag'
      ? !['pointer', 'mouse'].includes(beginObservation)
      : beginObservation !== 'none')
        || isAuthorizationCurrent(snapshot.authorization) !== true) return null;
    const atMs = readClock(snapshot.clock);
    if (atMs === null || isAuthorizationCurrent(snapshot.authorization) !== true) return null;
    const authorityCallbacks = Object.freeze([
      snapshot.isRouteCurrent,
      snapshot.isSeriesCurrent,
      snapshot.isGenerationCurrent,
      snapshot.isVisible,
      snapshot.isFocused,
      snapshot.isActiveViewport,
    ]);
    let cineId;
    const interaction = Object.freeze({});
    const state = {
      authorization: snapshot.authorization,
      clock: snapshot.clock,
      phase: snapshot.phase,
      originPhaseId: snapshot.originPhaseId,
      cineId: null,
      viewport: snapshot.viewport,
      seriesGeneration: snapshot.seriesGeneration,
      interaction: snapshot.interaction,
      direction: snapshot.direction,
      dragFamily: snapshot.interaction === 'drag' ? beginObservation : null,
      initialSlice: snapshot.initialSlice,
      lastAppliedTarget: snapshot.initialSlice,
      lastAtMs: atMs,
      authorityCallbacks,
      currentCandidate: null,
      currentCommand: null,
      inputs: [],
      applied: [],
      outstandingWorkIds: new Set(),
      quietTimer: null,
      latestActivityToken: null,
      latestActivityAtMs: null,
      release: null,
      transitionCount: 0,
      active: true,
      outcome: null,
      overflow: false,
    };
    state.proofKey = proofKeyFor(state);
    if (!authorityCurrent(state)) {
      state.active = false;
      state.outcome = 'failed';
      return null;
    }
    try { cineId = reserveOrdinal('cine'); } catch (_) {
      state.active = false;
      state.outcome = 'failed';
      return null;
    }
    if (!positiveInteger(cineId) || !authorityCurrent(state)) {
      state.active = false;
      state.outcome = 'failed';
      return null;
    }
    state.cineId = cineId;
    const priorOwner = activeOwnersByProofKey.get(state.proofKey);
    if (priorOwner?.active) close(priorOwner, 'replaced');
    activeOwnersByProofKey.set(state.proofKey, state);
    interactionStates.set(interaction, state);
    return interaction;
  };

  const scheduleWheelQuiet = (state, atMs) => {
    clearQuietTimer(state);
    const activityToken = Object.freeze({});
    state.latestActivityToken = activityToken;
    state.latestActivityAtMs = atMs;
    let timer = null;
    try {
      timer = setTimer(() => {
        if (state.quietTimer !== timer || state.latestActivityToken !== activityToken
            || state.latestActivityAtMs !== atMs) return;
        state.quietTimer = null;
        if (!stateIsCurrent(state) || state.release
            || state.inputs.length === 0) return;
        const releaseAtMs = readMonotonic(state, false);
        if (releaseAtMs === null || releaseAtMs - atMs < 150 || !authorityCurrent(state)) {
          close(state, 'failed');
          return;
        }
        let browserOrdinal;
        try { browserOrdinal = reserveOrdinal('cineBrowser'); } catch (_) {
          close(state, 'failed');
          return;
        }
        const row = append(state, {
          type: 'cine-release', eventKind: RELEASE_EVENT_KINDS.wheel, browserOrdinal, atMs: releaseAtMs,
        });
        if (!row) {
          close(state, state.transitionCount >= MAX_NON_TERMINAL_TRANSITIONS ? 'overflow' : 'failed');
          return;
        }
        state.release = Object.freeze({
          browserOrdinal, atMs: releaseAtMs, eventKind: RELEASE_EVENT_KINDS.wheel,
        });
        state.latestActivityToken = null;
        state.latestActivityAtMs = null;
        maybeSettle(state);
      }, 150);
      state.quietTimer = timer;
    } catch (_) {
      close(state, 'failed');
    }
  };

  const observeWheelActivityReceipt = (interaction, event, zeroOnly) => {
    let state = null;
    try { state = interactionStates.get(interaction); } catch (_) { state = null; }
    if (!stateIsCurrent(state) || state.release || state.interaction !== 'wheel') return null;
    let trustedObservation = null;
    try {
      trustedObservation = zeroOnly
        ? observeTrustedZeroWheel(event, state.clock)
        : observeTrustedInput(event, state.clock, 'wheel');
    } catch (_) {
      trustedObservation = null;
    }
    // Bind before advancing the monotonic clock, quiet timer, or activity ordinal.
    // A replayed/foreign event therefore cannot mutate diagnostic authority.
    if (trustedObservation !== 'wheel' || !authorityCurrent(state)
        || !bindTrustedActivityEvent(state, event)) return null;
    const atMs = readMonotonic(state, false);
    if (atMs === null || !authorityCurrent(state)) {
      try { trustedEventBindings.delete(event); } catch (_) {}
      return null;
    }
    let activityOrdinal;
    try { activityOrdinal = reserveOrdinal('cineActivity'); } catch (_) {
      close(state, 'failed');
      return null;
    }
    if (!positiveInteger(activityOrdinal) || !authorityCurrent(state)) {
      close(state, 'failed');
      return null;
    }
    scheduleWheelQuiet(state, atMs);
    if (!stateIsCurrent(state) || state.release || state.latestActivityAtMs !== atMs) return null;
    const receipt = Object.freeze({});
    activityReceiptStates.set(receipt, {
      state, activityOrdinal, atMs, pending: true,
    });
    return receipt;
  };

  const observeWheelActivityWithReceipt = (interaction, event) => (
    observeWheelActivityReceipt(interaction, event, false)
  );

  const observeZeroWheelActivityWithReceipt = (interaction, event) => (
    observeWheelActivityReceipt(interaction, event, true)
  );

  const observeWheelActivity = (interaction, event) => (
    observeWheelActivityWithReceipt(interaction, event) !== null
  );

  const activityReceiptState = receipt => {
    let receiptState = null;
    try { receiptState = activityReceiptStates.get(receipt); } catch (_) { receiptState = null; }
    return receiptState || null;
  };

  const clearActivityReceipt = receipt => {
    const receiptState = activityReceiptState(receipt);
    if (!receiptState || receiptState.pending !== true) return false;
    receiptState.pending = false;
    return true;
  };

  const observeInput = (interaction, options) => {
    let state = null;
    try { state = interactionStates.get(interaction); } catch (_) { state = null; }
    if (!stateIsCurrent(state) || state.release) return null;
    if (state.inputs.length >= MAX_CANDIDATES) {
      close(state, 'overflow');
      return null;
    }
    const snapshot = exactData(options, ['event', 'targetBefore', 'targetAfter']);
    // Browser clocks are quantized; retained sequence/browser ordinals provide strict ordering.
    const atMs = readMonotonic(state, false);
    let trustedObservation = null;
    try {
      trustedObservation = snapshot
        ? observeTrustedInput(snapshot.event, state.clock, state.interaction) : null;
    } catch (_) {
      trustedObservation = null;
    }
    if (!snapshot || atMs === null
        || (state.interaction === 'wheel' && trustedObservation !== 'wheel')
        || (state.interaction === 'slider' && trustedObservation !== 'range')
        || (state.interaction === 'drag' && trustedObservation !== state.dragFamily)
        || !nonNegativeInteger(snapshot.targetBefore) || !nonNegativeInteger(snapshot.targetAfter)
        || snapshot.targetBefore !== state.lastAppliedTarget || snapshot.targetBefore === snapshot.targetAfter
        || (state.direction === 'forward'
          ? snapshot.targetAfter <= snapshot.targetBefore : snapshot.targetAfter >= snapshot.targetBefore)
        || !authorityCurrent(state)) {
      close(state, 'failed');
      return null;
    }
    if (!consumeTrustedInputEvent(state, snapshot.event)) {
      close(state, 'failed');
      return null;
    }
    const transitionNeed = state.currentCandidate?.status === 'pending' ? 2 : 1;
    if (state.transitionCount + transitionNeed > MAX_NON_TERMINAL_TRANSITIONS) {
      close(state, 'overflow');
      return null;
    }
    let inputOrdinal;
    let browserOrdinal;
    let phaseWorkId;
    try {
      inputOrdinal = reserveOrdinal('cineInput');
      browserOrdinal = reserveOrdinal('cineBrowser');
      phaseWorkId = reserveOrdinal('cineWork');
    } catch (_) {
      close(state, 'failed');
      return null;
    }
    const previous = state.currentCandidate;
    if (previous?.status === 'pending') {
      previous.status = 'coalesced';
      if (!append(state, {
        type: 'cine-input-coalesced', inputOrdinal: previous.inputOrdinal,
        supersededByInputOrdinal: inputOrdinal, atMs,
      })) {
        close(state, 'overflow');
        return null;
      }
    }
    const input = Object.freeze({});
    const inputState = {
      input,
      state,
      inputOrdinal,
      browserOrdinal,
      atMs,
      targetBefore: snapshot.targetBefore,
      targetAfter: snapshot.targetAfter,
      status: 'pending',
      command: null,
      renderOperation: null,
      publication: null,
      painted: false,
    };
    inputStates.set(input, inputState);
    state.currentCandidate = inputState;
    state.inputs.push(inputState);
    if (!append(state, {
      type: 'cine-input', inputOrdinal, eventKind: INPUT_EVENT_KINDS[state.interaction],
      isTrusted: true, browserOrdinal, phaseWorkId, atMs,
      targetBefore: inputState.targetBefore, targetAfter: inputState.targetAfter,
    })) {
      close(state, 'overflow');
      return null;
    }
    if (state.interaction === 'wheel') scheduleWheelQuiet(state, atMs);
    return input;
  };

  const consumeInput = (input, targetSlice) => {
    let inputState = null;
    try { inputState = inputStates.get(input); } catch (_) { inputState = null; }
    if (!inputState || inputState.status !== 'pending' || !nonNegativeInteger(targetSlice)
        || targetSlice !== inputState.targetAfter) return null;
    const state = inputState.state;
    if (!stateIsCurrent(state) || state.currentCandidate !== inputState || state.release) return null;
    const atMs = readMonotonic(state, false);
    const previousCommand = state.currentCommand;
    const transitionNeed = previousCommand?.status === 'applied' ? 2 : 1;
    if (atMs === null || !authorityCurrent(state)
        || state.transitionCount + transitionNeed > MAX_NON_TERMINAL_TRANSITIONS) {
      close(state, state.transitionCount + transitionNeed > MAX_NON_TERMINAL_TRANSITIONS
        ? 'overflow' : 'failed');
      return null;
    }
    if (previousCommand?.status === 'applied') {
      if (previousCommand.renderOperation || previousCommand.publication
          || !state.outstandingWorkIds.has(previousCommand.workId)) {
        close(state, 'failed');
        return null;
      }
      previousCommand.status = 'coalesced';
      previousCommand.inputState.status = 'coalesced';
      state.outstandingWorkIds.delete(previousCommand.workId);
      if (!append(state, {
        type: 'cine-command-coalesced',
        inputOrdinal: previousCommand.inputState.inputOrdinal,
        commandWorkId: previousCommand.workId,
        supersededByInputOrdinal: inputState.inputOrdinal,
        atMs,
      })) {
        close(state, 'overflow');
        return null;
      }
    }
    let workId;
    try { workId = reserveOrdinal('cineWork'); } catch (_) {
      close(state, 'failed');
      return null;
    }
    const command = Object.freeze({});
    const commandState = {
      command,
      inputState,
      workId,
      status: 'applied',
      renderBinding: null,
      renderOperation: null,
      publication: null,
    };
    commandStates.set(command, commandState);
    inputState.command = commandState;
    inputState.status = 'applied';
    state.currentCandidate = null;
    state.currentCommand = commandState;
    state.applied.push(inputState);
    state.lastAppliedTarget = inputState.targetAfter;
    state.outstandingWorkIds.add(workId);
    if (!append(state, {
      type: 'cine-input-applied', inputOrdinal: inputState.inputOrdinal,
      commandWorkId: workId, atMs,
      targetBefore: inputState.targetBefore, targetAfter: inputState.targetAfter,
    })) {
      close(state, 'overflow');
      return null;
    }
    return command;
  };

  const release = (interaction, options) => {
    let state = null;
    try { state = interactionStates.get(interaction); } catch (_) { state = null; }
    if (!stateIsCurrent(state) || state.release || state.interaction === 'wheel') return false;
    const snapshot = exactData(options, ['event']);
    const atMs = readMonotonic(state, false);
    let trustedObservation = null;
    try {
      trustedObservation = snapshot
        ? observeTrustedRelease(snapshot.event, state.clock, state.interaction) : null;
    } catch (_) {
      trustedObservation = null;
    }
    if (!snapshot || atMs === null
        || (state.interaction === 'drag' && trustedObservation !== state.dragFamily)
        || (state.interaction === 'slider' && !['pointer', 'mouse'].includes(trustedObservation))
        || !authorityCurrent(state)) {
      close(state, 'failed');
      return false;
    }
    let browserOrdinal;
    try { browserOrdinal = reserveOrdinal('cineBrowser'); } catch (_) {
      close(state, 'failed');
      return false;
    }
    const eventKind = RELEASE_EVENT_KINDS[state.interaction];
    if (!append(state, { type: 'cine-release', eventKind, browserOrdinal, atMs })) {
      close(state, state.transitionCount >= MAX_NON_TERMINAL_TRANSITIONS ? 'overflow' : 'failed');
      return false;
    }
    state.release = Object.freeze({ browserOrdinal, atMs, eventKind });
    maybeSettle(state);
    return true;
  };

  const createRenderBinding = command => {
    let commandState = null;
    try { commandState = commandStates.get(command); } catch (_) { commandState = null; }
    const inputState = commandState?.inputState;
    const state = inputState?.state;
    if (!commandState || !stateIsCurrent(state) || state.currentCommand !== commandState
        || commandState.status !== 'applied' || inputState.status !== 'applied'
        || commandState.renderBinding || commandState.renderOperation || commandState.publication
        || state.release || !authorityCurrent(state)) return null;
    const capability = Object.freeze({});
    const bindingState = Object.freeze({
      capability,
      commandState,
      authorization: state.authorization,
      phase: state.phase,
      originPhaseId: state.originPhaseId,
      viewport: state.viewport,
      seriesGeneration: state.seriesGeneration,
      interaction: state.interaction,
      targetSlice: inputState.targetAfter,
      direction: state.direction,
    });
    commandState.renderBinding = bindingState;
    renderBindingStates.set(capability, bindingState);
    return capability;
  };

  const prepareRenderBinding = (binding, context, authorization) => {
    let bindingState = null;
    try { bindingState = renderBindingStates.get(binding); } catch (_) { bindingState = null; }
    const commandState = bindingState?.commandState;
    const inputState = commandState?.inputState;
    const state = inputState?.state;
    if (!bindingState || !commandState || !stateIsCurrent(state)
        || bindingState.capability !== binding
        || bindingState.commandState !== commandState
        || bindingState.authorization !== authorization
        || state.authorization !== authorization
        || state.currentCommand !== commandState
        || commandState.renderBinding !== bindingState
        || commandState.status !== 'applied' || inputState.status !== 'applied'
        || commandState.renderOperation || commandState.publication || state.release
        || bindingState.phase !== state.phase || bindingState.phase !== 'cine'
        || bindingState.phase !== context?.phase
        || bindingState.originPhaseId !== state.originPhaseId
        || bindingState.viewport !== state.viewport
        || bindingState.viewport !== context?.viewport
        || bindingState.seriesGeneration !== state.seriesGeneration
        || bindingState.seriesGeneration !== context?.generation
        || bindingState.interaction !== state.interaction
        || bindingState.interaction !== context?.interaction
        || bindingState.targetSlice !== inputState.targetAfter
        || bindingState.targetSlice !== context?.slice
        || bindingState.direction !== state.direction
        || !authorityCurrent(state)) return null;
    return commandState;
  };

  const claimRenderOperation = (commandState, operation) => {
    const inputState = commandState?.inputState;
    const state = inputState?.state;
    if (!commandState || !operation || !stateIsCurrent(state)
        || state.currentCommand !== commandState || state.release
        || commandState.status !== 'applied' || inputState.status !== 'applied'
        || commandState.renderOperation || commandState.publication
        || !authorityCurrent(state)) return false;
    commandState.renderOperation = operation;
    inputState.renderOperation = operation;
    return true;
  };

  const acceptRenderPublication = (command, operation, details, revalidateRender) => {
    let commandState = null;
    try { commandState = commandStates.get(command); } catch (_) { commandState = null; }
    const detailsSnapshot = exactData(details,
      ['source', 'generation', 'requestGeneration', 'slice', 'surface']);
    const inputState = commandState?.inputState;
    const state = inputState?.state;
    if (!commandState || !stateIsCurrent(state) || state.currentCommand !== commandState
        || commandState.status !== 'applied' || inputState.status !== 'applied'
        || commandState.renderOperation !== operation || inputState.renderOperation !== operation
        || commandState.publication || inputState.publication
        || !detailsSnapshot || detailsSnapshot.generation !== state.seriesGeneration
        || !positiveInteger(detailsSnapshot.requestGeneration)
        || detailsSnapshot.slice !== inputState.targetAfter || !detailsSnapshot.source
        || !['preview', 'active-image'].includes(detailsSnapshot.surface)
        || typeof revalidateRender !== 'function') return null;
    let renderCurrent = false;
    try { renderCurrent = revalidateRender() === true; } catch (_) { renderCurrent = false; }
    if (!renderCurrent || !authorityCurrent(state)) return null;
    try { renderCurrent = revalidateRender() === true; } catch (_) { renderCurrent = false; }
    if (!renderCurrent || !stateIsCurrent(state) || state.currentCommand !== commandState) return null;
    const atMs = readMonotonic(state, false);
    let browserOrdinal;
    let generationOrdinal;
    let phaseWorkId;
    try { browserOrdinal = reserveOrdinal('cineBrowser'); } catch (_) { return null; }
    try { generationOrdinal = reserveOrdinal('cineGeneration'); } catch (_) { return null; }
    try { phaseWorkId = reserveOrdinal('cineWork'); } catch (_) { return null; }
    try { renderCurrent = revalidateRender() === true; } catch (_) { renderCurrent = false; }
    if (atMs === null || !renderCurrent || !authorityCurrent(state)) return null;
    const publication = Object.freeze({});
    const publicationState = {
      publication,
      commandState,
      operation,
      source: detailsSnapshot.source,
      generation: detailsSnapshot.generation,
      requestGeneration: detailsSnapshot.requestGeneration,
      slice: detailsSnapshot.slice,
      surface: detailsSnapshot.surface,
      frameStage: 0,
      frameRequestId: null,
      observationRequested: false,
      painted: false,
    };
    if (!append(state, {
      type: 'cine-render-publication',
      inputOrdinal: inputState.inputOrdinal,
      commandWorkId: commandState.workId,
      browserOrdinal,
      phaseWorkId,
      atMs,
      generationOrdinal,
      generation: detailsSnapshot.generation,
      requestGeneration: detailsSnapshot.requestGeneration,
      slice: detailsSnapshot.slice,
      surface: detailsSnapshot.surface,
      connected: true,
      nonzero: true,
      active: true,
      visible: true,
      focused: true,
      uncovered: true,
      sourcePresent: true,
      generationOwning: true,
    })) return null;
    commandState.publication = publicationState;
    inputState.publication = publicationState;
    publicationStates.set(publication, publicationState);
    return publication;
  };

  const requestPaintObservation = (publication, operation, details, revalidateRender,
    acceptAuthenticatedPaint = () => true) => {
    let publicationState = null;
    try { publicationState = publicationStates.get(publication); } catch (_) { publicationState = null; }
    const detailsSnapshot = exactData(details, ['source', 'generation']);
    const commandState = publicationState?.commandState;
    const inputState = commandState?.inputState;
    const state = inputState?.state;
    const observationLineageIsCurrent = () => Boolean(publicationState && stateIsCurrent(state)
      && state.currentCommand === commandState && commandState.status === 'applied'
      && commandState.renderOperation === operation && publicationState.operation === operation
      && commandState.publication === publicationState && inputState.publication === publicationState
      && !publicationState.painted && publicationState.frameStage <= 1
      && detailsSnapshot?.source === publicationState.source
      && detailsSnapshot?.generation === publicationState.generation);
    const observationIsCurrent = () => Boolean(
      publicationState?.observationRequested === true && observationLineageIsCurrent(),
    );
    if (!publicationState || !detailsSnapshot || !observationLineageIsCurrent()
        || publicationState.observationRequested || typeof revalidateRender !== 'function'
        || typeof acceptAuthenticatedPaint !== 'function') {
      if (state?.active) close(state, 'failed');
      return false;
    }
    let renderCurrent = false;
    try { renderCurrent = revalidateRender() === true; } catch (_) { renderCurrent = false; }
    if (!renderCurrent || !authorityCurrent(state)) {
      close(state, 'failed');
      return false;
    }
    try { renderCurrent = revalidateRender() === true; } catch (_) { renderCurrent = false; }
    if (!renderCurrent || !observationLineageIsCurrent()) {
      close(state, 'failed');
      return false;
    }
    publicationState.observationRequested = true;
    const failObservation = () => {
      close(state, 'failed');
      return false;
    };
    const acceptPaint = () => {
      if (!observationIsCurrent() || publicationState.frameStage !== 1
          || state.outstandingWorkIds.size !== 1
          || !state.outstandingWorkIds.has(commandState.workId)) return failObservation();
      let current = false;
      try { current = revalidateRender() === true; } catch (_) { current = false; }
      if (!current || !authorityCurrent(state) || !observationIsCurrent()) return failObservation();
      const atMs = readMonotonic(state, false);
      let browserOrdinal;
      let phaseWorkId;
      try { browserOrdinal = reserveOrdinal('cineBrowser'); } catch (_) { return failObservation(); }
      try { phaseWorkId = reserveOrdinal('cineWork'); } catch (_) { return failObservation(); }
      try { current = revalidateRender() === true; } catch (_) { current = false; }
      if (atMs === null || !current || !authorityCurrent(state) || !observationIsCurrent()
          || !state.outstandingWorkIds.has(commandState.workId)
          || !append(state, {
            type: 'cine-paint-confirmation',
            inputOrdinal: inputState.inputOrdinal,
            commandWorkId: commandState.workId,
            browserOrdinal,
            phaseWorkId,
            atMs,
            generation: publicationState.generation,
            slice: publicationState.slice,
            stableThroughPaint: true,
          })) return failObservation();
      publicationState.frameStage = 2;
      publicationState.painted = true;
      inputState.painted = true;
      commandState.status = 'painted';
      state.outstandingWorkIds.delete(commandState.workId);
      state.currentCommand = null;
      let authenticatedPaintAccepted = false;
      try {
        authenticatedPaintAccepted = acceptAuthenticatedPaint(Object.freeze({
          slice: publicationState.slice,
          requestGeneration: publicationState.requestGeneration,
        })) === true;
      } catch (_) {
        authenticatedPaintAccepted = false;
      }
      if (!authenticatedPaintAccepted) return failObservation();
      maybeSettle(state);
      return true;
    };
    let firstRequestId;
    const firstFrame = () => {
      if (publicationState.frameRequestId !== firstRequestId) return failObservation();
      publicationState.frameRequestId = null;
      if (!observationIsCurrent() || publicationState.frameStage !== 0) return failObservation();
      let current = false;
      try { current = revalidateRender() === true; } catch (_) { current = false; }
      if (!current || !authorityCurrent(state) || !observationIsCurrent()) return failObservation();
      publicationState.frameStage = 1;
      let secondRequestId;
      const secondFrame = () => {
        if (publicationState.frameRequestId !== secondRequestId) return failObservation();
        publicationState.frameRequestId = null;
        return acceptPaint();
      };
      try { secondRequestId = requestFrame(secondFrame); } catch (_) { secondRequestId = null; }
      if (secondRequestId == null) return failObservation();
      publicationState.frameRequestId = secondRequestId;
      if (!observationIsCurrent()) return failObservation();
      return true;
    };
    try { firstRequestId = requestFrame(firstFrame); } catch (_) { firstRequestId = null; }
    if (firstRequestId == null) return failObservation();
    publicationState.frameRequestId = firstRequestId;
    if (!observationIsCurrent()) return failObservation();
    return true;
  };

  const invalidateRenderOperation = (commandState, operation) => {
    const publicationState = commandState?.publication;
    if (!publicationState || publicationState.operation !== operation || publicationState.painted) return false;
    return close(commandState.inputState?.state, 'failed');
  };

  const maybeSettle = state => {
    if (!stateIsCurrent(state) || !state.release) return false;
    const applied = state.applied;
    const finalInput = applied.at(-1);
    const finalPublication = finalInput?.publication;
    if (state.currentCandidate || state.currentCommand
        || state.outstandingWorkIds.size !== 0) return false;
    const exactPublicationPaintParity = state.inputs.length === applied.length
      && applied.every((inputState, index) => state.inputs[index] === inputState
        && inputState.status === 'applied'
        && inputState.painted === true
        && inputState.command?.inputState === inputState
        && inputState.command.status === 'painted'
        && inputState.command.publication === inputState.publication
        && inputState.publication?.commandState === inputState.command
        && inputState.publication.painted === true
        && inputState.publication.slice === inputState.targetAfter);
    if (applied.length < 4 || !exactPublicationPaintParity || !finalPublication
        || finalInput.targetAfter !== state.lastAppliedTarget
        || finalPublication.slice !== state.lastAppliedTarget) {
      close(state, 'failed');
      return false;
    }
    if (!authorityCurrent(state)) {
      close(state, 'failed');
      return false;
    }
    const atMs = readMonotonic(state, false);
    let browserOrdinal;
    let phaseWorkId;
    if (atMs === null || atMs < state.release.atMs) {
      close(state, 'failed');
      return false;
    }
    try { browserOrdinal = reserveOrdinal('cineBrowser'); } catch (_) {
      close(state, 'failed');
      return false;
    }
    try { phaseWorkId = reserveOrdinal('cineWork'); } catch (_) {
      close(state, 'failed');
      return false;
    }
    if (!authorityCurrent(state) || !append(state, {
      type: 'cine-settlement',
      browserOrdinal,
      phaseWorkId,
      atMs,
      finalTargetSlice: state.lastAppliedTarget,
      finalRenderedSlice: finalPublication.slice,
      outstandingWorkIds: Object.freeze([]),
    })) {
      close(state, state.transitionCount >= MAX_NON_TERMINAL_TRANSITIONS ? 'overflow' : 'failed');
      return false;
    }
    state.active = false;
    state.outcome = 'settled';
    releaseProofKeyOwnership(state);
    return true;
  };

  const settle = interaction => {
    let state = null;
    try { state = interactionStates.get(interaction); } catch (_) { state = null; }
    return maybeSettle(state);
  };

  const snapshot = interaction => {
    let state = null;
    try { state = interactionStates.get(interaction); } catch (_) { state = null; }
    if (!state) return null;
    return Object.freeze({
      active: state.active,
      released: state.release !== null,
      settled: state.outcome === 'settled',
      failed: state.outcome === 'failed',
      cancelled: state.outcome === 'cancelled',
      overflow: state.overflow,
      transitionCount: state.transitionCount,
      observedInputs: state.inputs.length,
      appliedInputs: state.applied.length,
      outstandingWorkCount: state.outstandingWorkIds.size,
    });
  };

  const isActive = interaction => {
    let state = null;
    try { state = interactionStates.get(interaction); } catch (_) { state = null; }
    return stateIsCurrent(state);
  };

  const closeAuthorization = authorization => {
    let changed = false;
    // WeakMap is intentionally not iterable; bridge supplies tracked interaction
    // handles to cancel on session close through closeInteraction below.
    return changed;
  };

  const closeInteraction = (interaction, outcome = 'cancelled') => {
    let state = null;
    try { state = interactionStates.get(interaction); } catch (_) { state = null; }
    return close(state, outcome);
  };

  const brandDrain = drain => {
    retainedDrains.add(drain);
    return drain;
  };

  return Object.freeze({
    begin,
    observeInput,
    observeWheelActivity,
    observeWheelActivityWithReceipt,
    observeZeroWheelActivityWithReceipt,
    activityReceiptState,
    clearActivityReceipt,
    interactionState: interaction => interactionStates.get(interaction) || null,
    consumeInput,
    release,
    createRenderBinding,
    prepareRenderBinding,
    claimRenderOperation,
    acceptRenderPublication,
    requestPaintObservation,
    invalidateRenderOperation,
    settle,
    isActive,
    snapshot,
    closeInteraction,
    closeAuthorization,
    brandDrain,
    consumeDrain: drain => retainedDrains.delete(drain),
    ownsDrain: drain => retainedDrains.has(drain),
    ownsRow: row => retainedRows.has(row),
    inputState: input => inputStates.get(input) || null,
    commandState: command => commandStates.get(command) || null,
    publicationState: publication => publicationStates.get(publication) || null,
  });
};

export const VIEWPORT_CINE_INTERACTIONS = Object.freeze(['wheel', 'drag', 'slider']);
export const VIEWPORT_CINE_INPUT_EVENT_KINDS = INPUT_EVENT_KINDS;
export const VIEWPORT_CINE_RELEASE_EVENT_KINDS = RELEASE_EVENT_KINDS;
export const VIEWPORT_CINE_MAX_TRANSITIONS = MAX_TRANSITIONS;
